import { FC, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Clock, Info, MapPin, TrendingDown, TrendingUp, X } from "lucide-react";
import {
  HOUR_LABELS,
  WEEK_DAYS,
  type ShopifyMetric,
  type ShopifyRegion,
  type SparkPoint,
} from "./ShopifyGeoSpatialDashboard.data";
import { useShopifySalesAnalytics } from "../../hooks/useShopifySalesAnalytics";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardExportButton, DashboardNarrativePanel } from "src/components/common";
import DashboardSectionHeader from "src/components/common/DashboardSectionHeader";
import DefinitionTooltip, { type DefinitionTooltipInfo } from "src/components/common/DefinitionTooltip";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import { normalizeApiLang } from "src/utils/apiLang";
import { toArabicIndicDigitsInString } from "src/utils/numberFormat";
import { useLanguage } from "src/hooks/useLanguage";
import { formatDashboardValueString } from "src/utils/formatDashboardValue";
import type {
  RegionPanel,
  ShopifySalesCrossFilterRequest,
} from "src/services/ShopifySalesProductAPI";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import ShopifyCityMapLibre, {
  countMappableShopifyCities,
} from "../maps/ShopifyCityMapLibre";
import { CHART_COLOR_PALETTE, CHART_PRIMARY_COLOR } from "src/components/charts/chartPalette";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import "./ShopifyGeoSpatialDashboard.scss";

interface SparkChartPoint {
  x: number;
  y: number;
}

interface HoverState {
  index: number;
  period: "current" | "previous";
  clientX: number;
  clientY: number;
}

interface HeatmapHover {
  clientX: number;
  clientY: number;
  hour: string;
  day: string;
  orders: number;
}

interface DonutHover {
  clientX: number;
  clientY: number;
  name: string;
  value: number;
  formattedValue?: string;
}

interface ShopifyGeoSpatialDashboardProps {
  showHeader?: boolean;
}

type GeoCrossFilter = ShopifySalesCrossFilterRequest | null;
type PakistanShapeKey = "punjab" | "sindh" | "balochistan" | "kpk" | "ajk";

/** API panels may omit `id`; infer slug for MapLibre keys, React keys, and `filter_region`. */
function inferPakistanRegionSlug(
  rawId: string | undefined | null,
  title: string | undefined | null,
): string {
  const id = String(rawId ?? "")
    .trim()
    .toLowerCase();
  const tl = String(title ?? "").toLowerCase();
  const lookup = `${id} ${tl}`;

  if (
    !lookup.trim() ||
    lookup.includes("others") ||
    lookup.includes("other") ||
    lookup.includes("unknown") ||
    lookup.includes("unassigned") ||
    lookup.includes("uncategorized") ||
    lookup.includes("unmapped") ||
    lookup.includes("undefined") ||
    lookup.includes(" null ") ||
    lookup.trim() === "null" ||
    lookup.includes("n/a") ||
    lookup.includes("not set") ||
    lookup.includes("no region") ||
    lookup.includes("no_region") ||
    lookup.includes("no-region") ||
    lookup.includes("no location") ||
    lookup.includes("no_location") ||
    lookup.includes("no-location")
  ) {return "others";}
  if (lookup.includes("punjab")) {return "punjab";}
  if (lookup.includes("sindh")) {return "sindh";}
  if (lookup.includes("baloch")) {return "balochistan";}
  if (lookup.includes("kpk") || lookup.includes("khyber")) {return "kpk";}
  if (lookup.includes("federal") || lookup.includes("islamabad")) {return "federal";}
  return "ajk";
}

const isPakistanShapeKey = (value: string): value is PakistanShapeKey =>
  value === "punjab" ||
  value === "sindh" ||
  value === "balochistan" ||
  value === "kpk" ||
  value === "ajk";

const resolveRegionShapeKey = (region: ShopifyRegion): PakistanShapeKey => {
  const slug = inferPakistanRegionSlug(region.id, region.title);
  return isPakistanShapeKey(slug) ? slug : "ajk";
};

const isOthersRegion = (region: Pick<ShopifyRegion, "id" | "title">): boolean =>
  inferPakistanRegionSlug(region.id, region.title) === "others";

const hasNonZeroCityValue = (city: RegionPanel["cities"][number]): boolean =>
  Math.abs(Number(city.net_revenue) || 0) +
    Math.abs(Number(city.customers ?? city.customer_count) || 0) +
    Math.abs(Number(city.orders ?? city.order_count) || 0) >
  0;

const REGION_DISPLAY_ORDER: Record<string, number> = {
  punjab: 0,
  sindh: 1,
  balochistan: 2,
  kpk: 3,
  ajk: 4,
  federal: 5,
  others: 6,
};

const REGION_SLUG_FALLBACK_TITLE: Record<string, string> = {
  punjab: "Punjab",
  sindh: "Sindh",
  balochistan: "Balochistan",
  kpk: "KPK",
  ajk: "AJK + Gilgit",
  federal: "Federal",
  others: "Others",
};

const normalizeGeoMetricKey = (value: string) =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");


const GEO_KPI_TOOLTIP_KEYS: Record<string, string> = {
  netrevenue: "totalSales",
  totalsales: "totalSales",
  revenue: "totalSales",
  orders: "orders",
  netquantity: "netQuantity",
  discount: "discounts",
  discounts: "discounts",
  returns: "returns",
  refunds: "returns",
  returncount: "returnOrders",
};

const getGeoMetricDefinition = (
  metric: ShopifyMetric,
  scopeLabel: string,
  t: TFunction,
): DefinitionTooltipInfo => {
  const entry =
    GEO_KPI_TOOLTIP_KEYS[normalizeGeoMetricKey(metric.key)] ??
    GEO_KPI_TOOLTIP_KEYS[normalizeGeoMetricKey(metric.title)] ??
    "totalSales";
  const scope = scopeLabel.trim() || t("shopifyGeo.tooltips.scopeFallback");
  const formula = t(`shopifyGeo.kpiTooltips.${entry}.formula`, { scope, defaultValue: "" });

  return {
    description: t(`shopifyGeo.kpiTooltips.${entry}.description`, { scope }),
    ...(formula ? { formula } : {}),
  };
};

const getGeoMetricDisplayTitle = (metric: ShopifyMetric, t: TFunction) => {
  const normalizedKey = normalizeGeoMetricKey(metric.key);
  const normalizedTitle = normalizeGeoMetricKey(metric.title);
  if (["netrevenue", "revenue", "totalsales"].includes(normalizedKey) || normalizedTitle.includes("netrevenue")) {
    return t("shopifyGeo.kpi.totalSales");
  }
  if (normalizedKey === "discount") {
    return t("shopifyGeo.kpi.discounts");
  }
  if (normalizedKey === "refunds") {
    return t("shopifyGeo.kpi.returns");
  }
  if (normalizedTitle === "returncount" || normalizedKey === "returncount") {
    return t("shopifyGeo.kpi.returnOrders");
  }
  return metric.title;
};

/** Human-readable region for filter chip (panel title when data loaded, else slug fallback). */
function regionLabelForChip(
  regionSlug: string | undefined | null,
  panels: ShopifyRegion[],
): string {
  const s = String(regionSlug ?? "").trim().toLowerCase();
  if (!s) {
    return "";
  }
  const fromPanel = panels.find(p => inferPakistanRegionSlug(p.id, p.title) === s);
  if (fromPanel) {
    return fromPanel.title;
  }
  return REGION_SLUG_FALLBACK_TITLE[s] ?? s.charAt(0).toUpperCase() + s.slice(1);
}

function toCityFilterSlug(name: string): string {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function applyCityCrossFilter(
  prev: GeoCrossFilter,
  region: string,
  city: string,
): GeoCrossFilter {
  return {
    filter_region: region,
    filter_city: toCityFilterSlug(city),
    filter_hour: prev?.filter_hour,
    filter_weekday: prev?.filter_weekday,
  };
}

function applyHeatmapCrossFilter(
  prev: GeoCrossFilter,
  region: string,
  hour: number,
  weekday: number,
): GeoCrossFilter {
  return {
    filter_region: region,
    filter_city: prev?.filter_city,
    filter_hour: hour,
    filter_weekday: weekday,
  };
}

function hasCrossFilterParams(f: GeoCrossFilter): boolean {
  return Boolean(f?.filter_region?.trim());
}

function formatGeoFilterChipText(
  filter: {
    region?: string | null;
    city?: string | null;
    hour?: number | null;
    weekday?: number | null;
  },
  t: TFunction,
  weekDays: string[],
  hourLabels: string[],
  panels: ShopifyRegion[],
  cityDisplayName?: string | null,
): string {
  const regionSlug = filter.region?.trim();
  if (!regionSlug) {
    return "";
  }
  const region = regionLabelForChip(regionSlug, panels).trim() || "—";
  const city = (cityDisplayName ?? filter.city)?.trim() ?? "";
  const hasCity = Boolean(city);
  const hasHeatmap =
    typeof filter.hour === "number" && typeof filter.weekday === "number";

  if (hasCity && hasHeatmap) {
    const day = weekDays[filter.weekday!] ?? "";
    const hr = hourLabels[filter.hour!] ?? "";
    return t("shopifyGeo.crossFilter.stacked", { region, city, day, hour: hr });
  }
  if (hasCity) {
    return t("shopifyGeo.crossFilter.city", { region, city });
  }
  if (hasHeatmap) {
    const day = weekDays[filter.weekday!] ?? "";
    const hr = hourLabels[filter.hour!] ?? "";
    return t("shopifyGeo.crossFilter.heatmap", { region, day, hour: hr });
  }
  return region;
}

const normalizeCity = (name: string) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function titleCaseWords(value: string): string {
  return String(value || "")
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function resolveCityDisplayName(
  citySlug: string | null | undefined,
  cities?: ShopifyRegion["cities"],
): string | null {
  const raw = citySlug?.trim();
  if (!raw) {
    return null;
  }
  const normalized = normalizeCity(raw);
  const slug = toCityFilterSlug(raw);
  const fromList = cities?.find(city => {
    const cityNorm = normalizeCity(city.city);
    return cityNorm === normalized || toCityFilterSlug(city.city) === slug;
  });
  if (fromList?.city?.trim()) {
    return fromList.city.trim();
  }
  return titleCaseWords(raw.replace(/-/g, " "));
}

/** Same palette as the sales-analytics API uses for top_products so colors stay consistent. */
const TOP_PRODUCT_PALETTE = CHART_COLOR_PALETTE;
const PRODUCT_BAR_RANK_COLORS = [
  "#2b74e4",
  "#2fc7b5",
  "#a64fee",
  "#5d97ee",
  "#12b886",
] as const;

function stableColorForProductName(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (Math.imul(31, h) + name.charCodeAt(i)) | 0;
  }
  return TOP_PRODUCT_PALETTE[Math.abs(h) % TOP_PRODUCT_PALETTE.length];
}

function transformApiRegionToShopifyRegion(panel: RegionPanel, t: TFunction): ShopifyRegion {
  const slug = inferPakistanRegionSlug(panel.id, panel.title);
  const title = slug === "others"
    ? t("shopifyGeo.regions.others", { defaultValue: "Others" })
    : panel.title;
  const heatData = panel.heatmap?.data ?? [];
  const maxHeatmapVal = Math.max(1, ...heatData.flat());

  const heatmapWithLevels = heatData.map(row =>
    row.map(val => {
      if (val === 0) {return 0;}
      return Math.min(4, Math.max(1, Math.ceil((val / maxHeatmapVal) * 4)));
    })
  );

  const metrics: ShopifyMetric[] = (panel.kpis ?? []).map(kpi => ({
    key: kpi.key,
    title: kpi.title,
    value: formatDashboardValueString(kpi.value),
    delta: kpi.delta ?? 0,
    deltaLabel: kpi.delta_label,
    previousDate: kpi.previous_date,
    currentDate: kpi.current_date,
    spark: Array.isArray(kpi.spark) ? kpi.spark : [],
  }));

  return {
    id: slug,
    title,
    cityMapTitle: t("shopifyGeo.panel.cityMapTitle", { region: title }),
    heatmapTitle: t("shopifyGeo.panel.heatmapTitle", { region: title }),
    donutTitle: t("shopifyGeo.panel.donutTitle", { region: title }),
    metrics,
    cities: (panel.cities ?? []).filter(city => hasNonZeroCityValue(city)),
    heatmap: heatmapWithLevels,
    heatmapRaw: heatData,
    products: (panel.top_products ?? []).filter(p => Number(p.value) > 0).map(p => ({
      name: p.name,
      value: p.value,
      valueFormattedValue: p.valueFormattedValue,
      color: stableColorForProductName(p.name),
    })),
  };
}

const sparkPoints = (
  values: SparkPoint[],
  key: "current" | "previous",
  max: number
): SparkChartPoint[] => {
  const step = values.length > 1 ? 100 / (values.length - 1) : 100;
  return values.map((point, index) => ({
    x: Number((index * step).toFixed(2)),
    y: Number((100 - (point[key] / max) * 100).toFixed(2)),
  }));
};

const toPolyline = (points: SparkChartPoint[]) => {
  return points.map(point => `${point.x},${point.y}`).join(" ");
};

const polarToCartesian = (cx: number, cy: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleInRadians),
    y: cy + radius * Math.sin(angleInRadians),
  };
};

const describeDonutArc = (
  cx: number,
  cy: number,
  outerRadius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number
): string => {
  const sweep = endAngle - startAngle;
  // A single SVG elliptical arc cannot span 360° (start and end coincide), so the path
  // collapses and the segment disappears — e.g. when a product filter leaves one top product.
  if (sweep >= 359.99) {
    const mid = startAngle + 180;
    return [
      describeDonutArc(cx, cy, outerRadius, innerRadius, startAngle, mid),
      describeDonutArc(cx, cy, outerRadius, innerRadius, mid, endAngle),
    ].join(" ");
  }

  const outerStart = polarToCartesian(cx, cy, outerRadius, endAngle);
  const outerEnd = polarToCartesian(cx, cy, outerRadius, startAngle);
  const innerStart = polarToCartesian(cx, cy, innerRadius, startAngle);
  const innerEnd = polarToCartesian(cx, cy, innerRadius, endAngle);
  const largeArcFlag = sweep <= 180 ? "0" : "1";

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerStart.x} ${innerStart.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${innerEnd.x} ${innerEnd.y}`,
    "Z",
  ].join(" ");
};

const getFixedTooltipPosition = (
  clientX: number,
  clientY: number,
  tooltipWidth: number,
  tooltipHeight = 66,
  horizontalOffset = 12,
  minViewportLeft = 8,
  isRTL = false,
) => {
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;
  const minLeft = isRTL ? 8 : minViewportLeft;
  const maxLeft = Math.max(
    minLeft,
    viewportWidth - tooltipWidth - (isRTL ? minViewportLeft : 8),
  );
  const preferredLeft = isRTL
    ? clientX - horizontalOffset - tooltipWidth
    : clientX + horizontalOffset;
  const left = Math.max(minLeft, Math.min(preferredLeft, maxLeft));
  const top = Math.max(8, Math.min(clientY - tooltipHeight - 10, viewportHeight - tooltipHeight - 8));

  return {
    left: `${left}px`,
    top: `${top}px`,
    width: `${tooltipWidth}px`,
    maxWidth: "calc(100vw - 16px)",
    boxSizing: "border-box" as const,
  };
};

const heatLevelClass = (value: number) => {
  if (value >= 3) {return "shopify-geo__heat-cell--lv4";}
  if (value === 2) {return "shopify-geo__heat-cell--lv3";}
  if (value === 1) {return "shopify-geo__heat-cell--lv2";}
  return "shopify-geo__heat-cell--lv1";
};

const MAX_VERTEX_MARKERS = 62;

function formatGeoDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${d.getFullYear()}`;
}


const geoDigits = (value: string | number, isRTL: boolean) =>
  isRTL ? toArabicIndicDigitsInString(value) : String(value);

const geoNumberText = (value: string | number, isRTL: boolean) =>
  isRTL ? `\u2066${toArabicIndicDigitsInString(value)}\u2069` : String(value);

const SparklineMetric: FC<{
  metric: ShopifyMetric;
  scopeLabel: string;
  selected?: boolean;
  onSelectKpi?: () => void;
}> = ({ metric, scopeLabel, selected, onSelectKpi }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const spark = metric.spark;
  const max = useMemo(
    () => Math.max(...spark.map(p => p.current), ...spark.map(p => p.previous), 1),
    [spark]
  );
  const currentPoints = useMemo(() => sparkPoints(spark, "current", max), [spark, max]);
  const previousPoints = useMemo(() => sparkPoints(spark, "previous", max), [spark, max]);
  const showVertexMarkers = spark.length > 1 && spark.length <= MAX_VERTEX_MARKERS;
  const isIncrease = metric.delta >= 0;
  const normalizedKey = normalizeGeoMetricKey(metric.key);
  const normalizedTitle = normalizeGeoMetricKey(metric.title);
  const isInvertedMetric =
    normalizedKey === "discount" ||
    normalizedKey === "returns" ||
    normalizedKey === "refunds" ||
    normalizedTitle === "returncount" ||
    normalizedTitle === "returns" ||
    normalizedTitle === "refunds";
  const isPositive = isInvertedMetric ? !isIncrease : isIncrease;
  const [hover, setHover] = useState<HoverState | null>(null);
  const metricTitle = getGeoMetricDisplayTitle(metric, t);
  const metricDefinition = getGeoMetricDefinition(metric, scopeLabel, t);

  const formatHoverValue = (value: number) => {
    const rounded = geoNumberText(Math.round(value).toLocaleString(), isRTL);
    if (metric.value.includes("PKR")) {
      return t("shopifyGeo.spark.pkrValue", { value: rounded });
    }
    if (metric.key === "orders") {
      return `${rounded} ${t("shopifyGeo.spark.orders")}`;
    }
    return rounded;
  };

  const handleSparkMove = (event: MouseEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const normalizedX = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const normalizedY = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
    const index = Math.max(
      0,
      Math.min(spark.length - 1, Math.round((normalizedX / 100) * (spark.length - 1)))
    );

    const currentDistance = Math.abs(normalizedY - currentPoints[index].y);
    const previousDistance = Math.abs(normalizedY - previousPoints[index].y);
    const period = currentDistance <= previousDistance ? "current" : "previous";

    setHover({ index, period, clientX: event.clientX, clientY: event.clientY });
  };

  const activePoint = hover ? spark[hover.index] : null;

  const bucketDateLabel = (point: SparkPoint, period: "current" | "previous") => {
    if (period === "current") {
      return point.current_start_date ?? point.label;
    }
    return point.previous_start_date ?? point.label;
  };

  const sparkPointDisplayValue = (point: SparkPoint, period: "current" | "previous") => {
    const formatted = period === "current" ? point.currentFormattedValue : point.previousFormattedValue;
    return formatted ?? formatHoverValue(point[period]);
  };

  return (
    <article
      className={`shopify-geo__metric-card${onSelectKpi ? " shopify-geo__metric-card--selectable" : ""}${selected ? " shopify-geo__metric-card--active" : ""}`}
      aria-label={metricTitle}
      role={onSelectKpi ? "button" : undefined}
      tabIndex={onSelectKpi ? 0 : undefined}
      onClick={onSelectKpi ? () => onSelectKpi() : undefined}
      onKeyDown={
        onSelectKpi
          ? e => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelectKpi();
              }
            }
          : undefined
      }
    >
      <div
        className="shopify-geo__metric-title-row"
        onClick={onSelectKpi ? e => e.stopPropagation() : undefined}
        onKeyDown={onSelectKpi ? e => e.stopPropagation() : undefined}
      >
        <p className="shopify-geo__metric-title">{metricTitle}</p>
        <DefinitionTooltip
          label={metricTitle}
          info={metricDefinition}
          preferSide={isRTL ? "left" : "right"}
          formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
          exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
        />
      </div>
      <p className="shopify-geo__metric-value">{metric.value}</p>
      <p className={`shopify-geo__metric-delta ${isPositive ? "shopify-geo__metric-delta--positive" : "shopify-geo__metric-delta--negative"}`}>
        {isIncrease ? <TrendingUp size={12} aria-hidden /> : <TrendingDown size={12} aria-hidden />}{" "}
        {metric.deltaLabel}
      </p>
      <div
        className="shopify-geo__spark-wrap"
        onClick={onSelectKpi ? e => e.stopPropagation() : undefined}
        onKeyDown={onSelectKpi ? e => e.stopPropagation() : undefined}
      >
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
          onMouseMove={handleSparkMove}
          onMouseLeave={() => setHover(null)}
        >
          <polyline
            points={toPolyline(previousPoints)}
            className="shopify-geo__spark-line shopify-geo__spark-line--previous"
            fill="none"
            stroke="#94d0b5"
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={toPolyline(currentPoints)}
            className="shopify-geo__spark-line shopify-geo__spark-line--current"
            fill="none"
            stroke="#2b74e4"
            strokeWidth={2}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          {showVertexMarkers &&
            previousPoints.map((pt, i) => (
              <circle
                key={`spark-vp-${i}`}
                cx={pt.x}
                cy={pt.y}
                r={1.35}
                className="shopify-geo__spark-vertex shopify-geo__spark-vertex--previous"
                fill="rgba(148, 208, 181, 0.32)"
                stroke="#94d0b5"
                strokeWidth={0.35}
              />
            ))}
          {showVertexMarkers &&
            currentPoints.map((pt, i) => (
              <circle
                key={`spark-vc-${i}`}
                cx={pt.x}
                cy={pt.y}
                r={1.35}
                className="shopify-geo__spark-vertex shopify-geo__spark-vertex--current"
                fill="#2b74e4"
                stroke="#8d4fd3"
                strokeWidth={0.35}
              />
            ))}
          {hover && (
            <>
              <circle
                cx={previousPoints[hover.index].x}
                cy={previousPoints[hover.index].y}
                r={hover.period === "previous" ? 4 : 2.7}
                className="shopify-geo__spark-dot shopify-geo__spark-dot--previous"
                fill="rgba(148, 208, 181, 0.22)"
                stroke="#94d0b5"
                strokeWidth={1.5}
              />
              <circle
                cx={currentPoints[hover.index].x}
                cy={currentPoints[hover.index].y}
                r={hover.period === "current" ? 4 : 2.7}
                className="shopify-geo__spark-dot shopify-geo__spark-dot--current"
                fill="#2b74e4"
                stroke="#8d4fd3"
                strokeWidth={1.5}
              />
            </>
          )}
        </svg>
        {hover && activePoint && createPortal(
          <div
            className="analytics-chart-tooltip analytics-chart-tooltip--shopify-kpi"
            style={getFixedTooltipPosition(hover.clientX, hover.clientY, 250, 66, 12, 8, isRTL)}
          >
            <div className="analytics-chart-tooltip__bar" />
            <div className="analytics-chart-tooltip__content">
              <span className="analytics-chart-tooltip__meta">
                {hover.period === "current"
                  ? t("shopifyGeo.tooltips.currentPeriodDate")
                  : t("shopifyGeo.tooltips.previousPeriodDate")}{" "}
                <strong>
                  {geoNumberText(formatGeoDate(bucketDateLabel(activePoint, hover.period)), isRTL)}
                </strong>
              </span>
              <span className="analytics-chart-tooltip__meta">
                {metricTitle}: <strong>{sparkPointDisplayValue(activePoint, hover.period)}</strong>
              </span>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </article>
  );
};

const formatProductBarValue = (value: number, isRTL = false) => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return geoNumberText(`${Number((value / 1_000_000).toFixed(1)).toLocaleString()}M`, isRTL);
  }
  if (abs >= 1_000) {
    return geoNumberText(`${Number((value / 1_000).toFixed(0)).toLocaleString()}k`, isRTL);
  }
  return geoNumberText(Math.round(value).toLocaleString(), isRTL);
};

const ProductSalesBarChart: FC<{
  products: ShopifyRegion["products"];
  onProductClick?: (_productName: string) => void;
}> = ({ products, onProductClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [hover, setHover] = useState<DonutHover | null>(null);
  const topProducts = useMemo(
    () => [...products].sort((a, b) => b.value - a.value).slice(0, 5),
    [products]
  );
  const maxValue = useMemo(
    () => Math.max(1, ...topProducts.map(product => product.value)),
    [topProducts],
  );
  const minValue = useMemo(
    () => Math.min(...topProducts.filter(product => product.value > 0).map(product => product.value), 1),
    [topProducts],
  );

  return (
    <div className="shopify-geo__product-bars" aria-label={t("shopifyGeo.donut.aria")}>
      {topProducts.length > 0 ? (
        topProducts.map((product, index) => {
          const width = calculateBarWidthPct(product.value, maxValue, minValue);
          const barColor = PRODUCT_BAR_RANK_COLORS[index % PRODUCT_BAR_RANK_COLORS.length];
          const rowContent = (
            <>
              <span className="shopify-geo__product-bar-meta">
                <span className="shopify-geo__product-bar-name">{product.name}</span>
                <strong>
                  {product.valueFormattedValue ?? formatProductBarValue(product.value, isRTL)}
                </strong>
              </span>
              <span className="shopify-geo__product-bar-track" aria-hidden>
                <span
                  className="shopify-geo__product-bar-fill"
                  style={{ width: `${width}%`, backgroundColor: barColor }}
                />
              </span>
            </>
          );
          const handleMouseMove = (event: MouseEvent<HTMLElement>) => {
            setHover({
              clientX: event.clientX,
              clientY: event.clientY,
              name: product.name,
              value: product.value,
              formattedValue: product.valueFormattedValue,
            });
          };

          if (!onProductClick) {
            return (
              <div
                key={`${product.name}-${product.value}`}
                className="shopify-geo__product-bar-row"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHover(null)}
              >
                {rowContent}
              </div>
            );
          }

          return (
            <button
              key={`${product.name}-${product.value}`}
              type="button"
              className="shopify-geo__product-bar-row shopify-geo__product-bar-row--interactive"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHover(null)}
              onClick={() => onProductClick(product.name)}
            >
              {rowContent}
            </button>
          );
        })
      ) : (
        <p className="shopify-geo__product-bars-empty">{t("shopifyGeo.productBars.empty")}</p>
      )}
      {hover && createPortal(
        <div
          className="analytics-chart-tooltip analytics-chart-tooltip--shopify-products"
          style={getFixedTooltipPosition(hover.clientX, hover.clientY, 280, 66, 12, 8, isRTL)}
        >
          <div className="analytics-chart-tooltip__bar" />
          <div className="analytics-chart-tooltip__content">
            <span className="analytics-chart-tooltip__meta">
              {t("shopifyGeo.tooltips.productType")} <strong>{hover.name}</strong>
            </span>
            <span className="analytics-chart-tooltip__meta">
              {t("shopifyGeo.tooltips.totalSales")}{" "}
              <strong>
                {hover.formattedValue ?? t("shopifyGeo.tooltips.totalSalesAmount", {
                  amount: geoNumberText(hover.value.toLocaleString(), isRTL),
                })}
              </strong>
            </span>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

const CityMapFallback: FC<{ shapeKey: PakistanShapeKey; cities: ShopifyRegion["cities"] }> = ({
  shapeKey,
  cities,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  return (
  <div className="shopify-geo__city-map" role="img" aria-label={t("shopifyGeo.cityMap.fallbackAria")}>
    <div className={`shopify-geo__blob shopify-geo__blob--${shapeKey}`} />
    {cities.length > 0 ? (
      cities.slice(0, 3).map((c, i) => {
        const sizeClass = i === 0 ? "lg" : i === 1 ? "md" : "sm";
        const title = t("shopifyGeo.cityMap.bubbleTitle", {
          city: c.city,
          amount: c.net_revenueFormattedValue
            ?? geoNumberText(c.net_revenue.toLocaleString(), isRTL),
        });
        const label = `${title} — ${t("shopifyGeo.tooltips.formulaTitle")}: ${t("shopifyGeo.tooltips.cityTotalSalesFormula")}`;
        return (
          <span
            key={c.city}
            className={`shopify-geo__bubble shopify-geo__bubble--${sizeClass}`}
            title={label}
            aria-label={label}
          />
        );
      })
    ) : (
      <>
        <span className="shopify-geo__bubble shopify-geo__bubble--lg" />
        <span className="shopify-geo__bubble shopify-geo__bubble--md" />
        <span className="shopify-geo__bubble shopify-geo__bubble--sm" />
      </>
    )}
  </div>
  );
};

const PakistanCitiesMap: FC<{
  region: ShopifyRegion;
  shapeKey: PakistanShapeKey;
  onCityPinClick?: (_city: string, _regionId: string) => void;
  selectedCityNormalized?: string | null;
  selectedRegionId?: string | null;
  crossFilterCityLabel?: string | null;
}> = ({ region, shapeKey, onCityPinClick, selectedCityNormalized, selectedRegionId, crossFilterCityLabel }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const regionSlug = inferPakistanRegionSlug(region.id, region.title);
  const mappable = useMemo(() => countMappableShopifyCities(region.cities), [region.cities]);

  const formatRevenue = useCallback(
    (amount: number) =>
      t("shopifyGeo.tooltips.totalSalesAmount", {
        amount: geoNumberText(Math.round(amount).toLocaleString(), isRTL),
      }),
    [isRTL, t],
  );

  if (mappable === 0) {
    return <CityMapFallback shapeKey={shapeKey} cities={region.cities} />;
  }

  const mapWrapperAria = crossFilterCityLabel?.trim()
    ? t("shopifyGeo.cityMap.mapAriaWithCity", {
        region: region.title,
        city: crossFilterCityLabel.trim(),
      })
    : t("shopifyGeo.cityMap.mapAria", { region: region.title });

  return (
    <div
      className="shopify-geo__city-map shopify-geo__city-map--maplibre"
      role="img"
      aria-label={mapWrapperAria}
    >
      <ShopifyCityMapLibre
        cities={region.cities}
        shapeKey={shapeKey}
        regionId={regionSlug}
        regionTitle={region.title}
        metricLabel={t("shopifyGeo.cityMap.metricLabel")}
        formatRevenue={formatRevenue}
        onCityPinClick={
          onCityPinClick ? city => onCityPinClick(city, regionSlug) : undefined
        }
        selectedCityNormalized={selectedCityNormalized}
        selectedRegionId={selectedRegionId}
        crossFilterCityLabel={crossFilterCityLabel}
      />
    </div>
  );
};

const NotAvailableMapPlaceholder: FC<{ label: string }> = ({ label }) => {
  const { t } = useTranslation();
  return (
    <div
      className="shopify-geo__city-map shopify-geo__city-map--not-available"
      role="img"
      aria-label={t("shopifyGeo.cityMap.notAvailableAria", {
        region: label,
        defaultValue: "{{region}} map is not available",
      })}
    >
      <Info size={20} aria-hidden />
      <span>{t("shopifyGeo.cityMap.notAvailable", { defaultValue: "Not Available" })}</span>
    </div>
  );
};

const RegionCard: FC<{
  region: ShopifyRegion;
  crossFilter: GeoCrossFilter;
  onCrossFilter: (_next: GeoCrossFilter) => void;
  weekDays: string[];
  hourLabels: string[];
  mapSelectedCityNormalized: string | null;
  mapSelectedRegionId: string | null;
  /** When this panel matches the active city cross-filter, localized titles include this city name. */
  filteredCityDisplayName: string | null;
  heatmapFilterLabel?: string | null;
  isFocusedView?: boolean;
}> = ({
  region,
  crossFilter,
  onCrossFilter,
  weekDays,
  hourLabels,
  mapSelectedCityNormalized,
  mapSelectedRegionId,
  filteredCityDisplayName,
  heatmapFilterLabel,
  isFocusedView = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [heatHover, setHeatHover] = useState<HeatmapHover | null>(null);
  const tooltipPreferSide = isRTL ? ("left" as const) : ("right" as const);
  const shapeKey = resolveRegionShapeKey(region);
  const regionSlug = inferPakistanRegionSlug(region.id, region.title);
  const isOthers = isOthersRegion(region);

  const panelTitles = useMemo(() => {
    if (!filteredCityDisplayName?.trim()) {
      return {
        regionHeading: region.title,
        cityMap: region.cityMapTitle,
        heatmap: region.heatmapTitle,
        donut: region.donutTitle,
      };
    }
    const city = filteredCityDisplayName.trim();
    return {
      regionHeading: city,
      cityMap: t("shopifyGeo.panel.cityMapTitleWithCity", {
        region: region.title,
        city,
      }),
      heatmap: t("shopifyGeo.panel.heatmapTitleWithCity", {
        region: region.title,
        city,
      }),
      donut: t("shopifyGeo.panel.donutTitleWithCity", {
        region: region.title,
        city,
      }),
    };
  }, [
    filteredCityDisplayName,
    region.title,
    region.cityMapTitle,
    region.heatmapTitle,
    region.donutTitle,
    t,
  ]);

  const regionSectionAria = filteredCityDisplayName?.trim()
    ? filteredCityDisplayName.trim()
    : t("shopifyGeo.regions.panelAria", {
        region: region.title,
        defaultValue: `${region.title} panel`,
      });

  const hasCityFilter = Boolean(filteredCityDisplayName?.trim());
  const hasHeatmapFilter = Boolean(heatmapFilterLabel?.trim());

  return (
    <section
      className={[
        "shopify-geo__region",
        isFocusedView ? "shopify-geo__region--focused" : "",
        hasCityFilter ? "shopify-geo__region--city-filter" : "",
        hasHeatmapFilter && !hasCityFilter ? "shopify-geo__region--heatmap-filter" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={regionSectionAria}
    >
      <div
        className={[
          "shopify-geo__region-top",
          hasCityFilter || hasHeatmapFilter ? "shopify-geo__region-top--filtered" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          className={`shopify-geo__region-icon shopify-geo__region-icon--${
            isOthers ? "others" : shapeKey
          }`}
          aria-hidden="true"
        />
        <div className="shopify-geo__region-heading">
          <h3>{panelTitles.regionHeading}</h3>
          {hasCityFilter ? (
            <span className="shopify-geo__region-badge">{region.title}</span>
          ) : null}
          {hasHeatmapFilter ? (
            <span className="shopify-geo__region-badge shopify-geo__region-badge--time">
              {heatmapFilterLabel}
            </span>
          ) : null}
        </div>
        {isOthers ? (
          <DefinitionTooltip
            label={panelTitles.regionHeading}
            info={{
              description: t("shopifyGeo.tooltips.othersRegion", {
                defaultValue:
                  "Customers with no region associated with them due to lack of available data fall in Others.",
              }),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        ) : null}
      </div>

      <div className="shopify-geo__metrics-grid">
        {region.metrics.map(metric => (
          <SparklineMetric
            key={metric.key}
            metric={metric}
            scopeLabel={panelTitles.regionHeading}
          />
        ))}
      </div>

      <div className="shopify-geo__panels-row">
      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.cityMap}</span>
          <DefinitionTooltip
            label={panelTitles.cityMap}
            info={{
              description: t("shopifyGeo.tooltips.cityTotalSales", {
                region: region.title,
              }),
              formula: t("shopifyGeo.tooltips.cityTotalSalesFormula"),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        {isOthers ? (
          <NotAvailableMapPlaceholder label={region.title} />
        ) : (
          <PakistanCitiesMap
            region={region}
            shapeKey={shapeKey}
            onCityPinClick={(city, filterRegion) =>
              onCrossFilter(applyCityCrossFilter(crossFilter, filterRegion, city))
            }
            selectedCityNormalized={mapSelectedCityNormalized}
            selectedRegionId={mapSelectedRegionId}
            crossFilterCityLabel={filteredCityDisplayName}
          />
        )}
      </div>

      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.heatmap}</span>
          <DefinitionTooltip
            label={panelTitles.heatmap}
            info={{
              description: t("shopifyGeo.tooltips.heatmapCrossFilter"),
              formula: t("shopifyGeo.tooltips.heatmapFormula"),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        <div className="shopify-geo__heatmap-wrap">
          <div className="shopify-geo__heat-days">
            <span />
            {weekDays.map(day => (
              <span key={day}>{day}</span>
            ))}
          </div>
          <div className="shopify-geo__heat-body">
            {region.heatmap.map((row, rowIndex) => (
              <div className="shopify-geo__heat-row" key={`${regionSlug}-row-${rowIndex}`}>
                <span>{hourLabels[rowIndex]}</span>
                {row.map((value, colIndex) => {
                  const rawOrders = region.heatmapRaw?.[rowIndex]?.[colIndex] ?? value;
                  const heatActive =
                    crossFilter?.filter_region === regionSlug &&
                    crossFilter.filter_hour === rowIndex &&
                    crossFilter.filter_weekday === colIndex;
                  return (
                    <div
                      key={`${regionSlug}-heat-${rowIndex}-${colIndex}`}
                      role="button"
                      tabIndex={0}
                      className={`shopify-geo__heat-cell ${heatLevelClass(value)}${heatActive ? " shopify-geo__heat-cell--active" : ""}`}
                      onMouseMove={event => {
                        setHeatHover({
                          clientX: event.clientX,
                          clientY: event.clientY,
                          hour: hourLabels[rowIndex],
                          day: weekDays[colIndex],
                          orders: rawOrders,
                        });
                      }}
                      onMouseLeave={() => setHeatHover(null)}
                      onClick={() =>
                        onCrossFilter(
                          applyHeatmapCrossFilter(
                            crossFilter,
                            regionSlug,
                            rowIndex,
                            colIndex,
                          ),
                        )
                      }
                      onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onCrossFilter(
                            applyHeatmapCrossFilter(
                              crossFilter,
                              regionSlug,
                              rowIndex,
                              colIndex,
                            ),
                          );
                        }
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
          {heatHover && createPortal(
            <div
              className="analytics-chart-tooltip analytics-chart-tooltip--shopify-heatmap"
              style={getFixedTooltipPosition(
                heatHover.clientX,
                heatHover.clientY,
                220,
                66,
                12,
                8,
                isRTL,
              )}
            >
              <div className="analytics-chart-tooltip__bar" />
              <div className="analytics-chart-tooltip__content">
                <span className="analytics-chart-tooltip__meta">
                  {heatHover.day} {heatHover.hour}
                </span>
                <span className="analytics-chart-tooltip__meta">
                  <strong>
                    {geoDigits(
                      t("shopifyGeo.tooltips.ordersCount", { count: heatHover.orders }),
                      isRTL,
                    )}
                  </strong>
                </span>
              </div>
            </div>,
            document.body,
          )}
        </div>
      </div>

      <div className="shopify-geo__panel">
        <h4>
          <span className="shopify-geo__panel-title-text">{panelTitles.donut}</span>
          <DefinitionTooltip
            label={panelTitles.donut}
            info={{
              description: t("shopifyGeo.tooltips.topProductsTotalSales", {
                region: region.title,
              }),
              formula: t("shopifyGeo.tooltips.topProductsFormula", { region: region.title }),
            }}
            preferSide={tooltipPreferSide}
            formulaTitle={t("shopifyGeo.tooltips.formulaTitle")}
            exampleLabel={t("shopifyGeo.tooltips.exampleLabel")}
          />
        </h4>
        <div className="shopify-geo__product-bars-block">
          <ProductSalesBarChart products={region.products} />
        </div>
      </div>
      </div>
    </section>
  );
};

type GeoLayoutWidth = "wide" | "compact" | "stacked";

const ShopifyGeoSpatialDashboard: FC<ShopifyGeoSpatialDashboardProps> = ({
  showHeader = true,
}) => {
  const { t, i18n } = useTranslation();
  const apiLang = normalizeApiLang(i18n.language);
  const todayISO = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [crossFilter, setCrossFilter] = useState<GeoCrossFilter>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [layoutWidth, setLayoutWidth] = useState<GeoLayoutWidth>("wide");

  /** Remember each product's color from full top-5 responses; filtered responses often repeat the first-slot color. */
  const productDonutColorCacheRef = useRef(new Map<string, string>());

  const apiCrossFilter = useMemo(() => crossFilter, [crossFilter]);

  const { data, loading, error } = useShopifySalesAnalytics(
    startDate,
    endDate,
    apiLang,
    apiCrossFilter,
  );

  const weekDays = useMemo(() => {
    const v = t("shopifyGeo.weekDays", { returnObjects: true });
    return Array.isArray(v) && v.length === 7 ? (v as string[]) : WEEK_DAYS;
  }, [t, i18n.language]);

  const hourLabels = useMemo(() => {
    const v = t("shopifyGeo.hourLabels", { returnObjects: true });
    return Array.isArray(v) && v.length === 24 ? (v as string[]) : HOUR_LABELS;
  }, [t, i18n.language]);

  const regions = useMemo(() => {
    if (!data?.regions?.length) {
      return [];
    }
    const cache = productDonutColorCacheRef.current;
    return data.regions
      .map(panel => {
        const slug = inferPakistanRegionSlug(panel.id, panel.title);
        const list = panel.top_products ?? [];

        if (list.length >= 2) {
          for (const p of list) {
            if (p.name) {
              cache.set(`${slug}::${p.name}`, stableColorForProductName(p.name));
            }
          }
        }

        const region = transformApiRegionToShopifyRegion(panel, t);
        const products = region.products.map(pr => {
          const key = `${slug}::${pr.name}`;
          const cached = cache.get(key);
          const color =
            cached ??
            stableColorForProductName(pr.name) ??
            CHART_PRIMARY_COLOR;
          return { ...pr, color };
        });

        return { ...region, products };
      })
      .sort((a, b) => {
        const aSlug = inferPakistanRegionSlug(a.id, a.title);
        const bSlug = inferPakistanRegionSlug(b.id, b.title);
        const aOrder = REGION_DISPLAY_ORDER[aSlug] ?? 99;
        const bOrder = REGION_DISPLAY_ORDER[bSlug] ?? 99;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return a.title.localeCompare(b.title);
      });
  }, [data, t, i18n.language]);

  const activeFilterState = useMemo(() => {
    if (crossFilter !== null) {
      return {
        region: crossFilter.filter_region?.trim() || null,
        citySlug: crossFilter.filter_city?.trim() || null,
        hour: typeof crossFilter.filter_hour === "number" ? crossFilter.filter_hour : null,
        weekday: typeof crossFilter.filter_weekday === "number" ? crossFilter.filter_weekday : null,
      };
    }
    return {
      region: data?.active_filter?.region?.trim() || null,
      citySlug: data?.active_filter?.city?.trim() || null,
      hour: data?.active_filter?.hour ?? null,
      weekday: data?.active_filter?.weekday ?? null,
    };
  }, [crossFilter, data?.active_filter]);

  const activeRegionSlug = useMemo(
    () => activeFilterState.region?.trim().toLowerCase() ?? null,
    [activeFilterState.region],
  );

  const focusedRegion = useMemo(() => {
    if (!activeRegionSlug) {
      return null;
    }
    return (
      regions.find(
        region => inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug,
      ) ?? null
    );
  }, [regions, activeRegionSlug]);

  const resolvedCityDisplayName = useMemo(
    () => resolveCityDisplayName(activeFilterState.citySlug, focusedRegion?.cities),
    [activeFilterState.citySlug, focusedRegion?.cities],
  );

  const heatmapFilterLabel = useMemo(() => {
    const { hour, weekday } = activeFilterState;
    if (typeof hour !== "number" || typeof weekday !== "number") {
      return null;
    }
    const day = weekDays[weekday] ?? "";
    const hr = hourLabels[hour] ?? "";
    if (!day || !hr) {
      return null;
    }
    return `${day} · ${hr}`;
  }, [activeFilterState, weekDays, hourLabels]);

  const filterRegionLabel = useMemo(() => {
    if (!activeRegionSlug) {
      return "";
    }
    return regionLabelForChip(activeRegionSlug, regions).trim();
  }, [activeRegionSlug, regions]);

  const filterChipText = useMemo(() => {
    const filterInput = {
      region: activeFilterState.region,
      city: activeFilterState.citySlug,
      hour: activeFilterState.hour,
      weekday: activeFilterState.weekday,
    };
    if (!filterInput.region) {
      return "";
    }
    return formatGeoFilterChipText(
      filterInput,
      t,
      weekDays,
      hourLabels,
      regions,
      resolvedCityDisplayName,
    );
  }, [
    activeFilterState,
    t,
    weekDays,
    hourLabels,
    regions,
    resolvedCityDisplayName,
  ]);

  const removeRegionFilter = useCallback(() => setCrossFilter(null), []);
  const removeCityFilter = useCallback(() => {
    const region = crossFilter?.filter_region ?? activeFilterState.region;
    if (!region) return;
    const hourVal = crossFilter?.filter_hour ?? activeFilterState.hour;
    const weekdayVal = crossFilter?.filter_weekday ?? activeFilterState.weekday;
    setCrossFilter({
      filter_region: region,
      filter_hour: typeof hourVal === "number" ? hourVal : undefined,
      filter_weekday: typeof weekdayVal === "number" ? weekdayVal : undefined,
    });
  }, [crossFilter, activeFilterState]);
  const removeTimeFilter = useCallback(() => {
    const region = crossFilter?.filter_region ?? activeFilterState.region;
    if (!region) return;
    const cityVal = crossFilter?.filter_city ?? activeFilterState.citySlug;
    setCrossFilter({
      filter_region: region,
      filter_city: cityVal || undefined,
    });
  }, [crossFilter, activeFilterState]);

  const mapSelectedCityNormalized = useMemo(() => {
    if (crossFilter?.filter_city) {
      return normalizeCity(crossFilter.filter_city);
    }
    const c = data?.active_filter?.city;
    return c ? normalizeCity(c) : null;
  }, [crossFilter, data?.active_filter?.city]);

  const mapSelectedRegionId = useMemo(() => activeRegionSlug, [activeRegionSlug]);

  const visibleRegions = useMemo(() => {
    if (!activeRegionSlug) {
      return regions;
    }
    const filtered = regions.filter(
      region => inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug,
    );
    return filtered.length ? filtered : regions;
  }, [regions, activeRegionSlug]);

  const isFocusedFilterView = visibleRegions.length === 1;

  const narrativePayloadData = useMemo(
    () => data && typeof data === "object" && !Array.isArray(data)
      ? data as unknown as Record<string, unknown>
      : {},
    [data],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "geo_spatial",
    tabKey: "overview",
    startDate,
    endDate,
    data: narrativePayloadData,
    enabled: !loading && !error && Boolean(data),
  });
  const narrativePanelLoading = loading || narrativeLoading || (!data && !error);

  const showFilterBar = Boolean(
    hasCrossFilterParams(crossFilter) || data?.active_filter?.region,
  );

  useEffect(() => {
    const node = mainRef.current;
    if (!node) {
      return;
    }

    const syncWidth = (width: number) => {
      if (width < 560) {
        setLayoutWidth("stacked");
      } else if (width < 960) {
        setLayoutWidth("compact");
      } else {
        setLayoutWidth("wide");
      }
    };

    syncWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        syncWidth(entry.contentRect.width);
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, showFilterBar, visibleRegions.length, isFocusedFilterView]);

  const formatDisplayDate = useCallback((dateInput: string | Date) => {
    const parsed = typeof dateInput === "string"
      ? new Date(`${dateInput}T00:00:00`)
      : dateInput;
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    return `${day}-${month}-${parsed.getFullYear()}`;
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (data?.date_range_label) {
      return data.date_range_label;
    }
    const { currentFrom, currentTo, previousFrom, previousTo } = (() => {
      if (data?.current_period && data?.comparison_period) {
        return {
          currentFrom: formatDisplayDate(data.current_period.start_date),
          currentTo: formatDisplayDate(data.current_period.end_date),
          previousFrom: formatDisplayDate(data.comparison_period.start_date),
          previousTo: formatDisplayDate(data.comparison_period.end_date),
        };
      }
      const startObj = new Date(`${startDate}T00:00:00`);
      const endObj = new Date(`${endDate}T00:00:00`);
      const periodDays = Math.max(1, Math.floor((endObj.getTime() - startObj.getTime()) / (24 * 60 * 60 * 1000)) + 1);
      const prevEnd = new Date(startObj);
      prevEnd.setDate(prevEnd.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevStart.getDate() - (periodDays - 1));
      return {
        currentFrom: formatDisplayDate(startDate),
        currentTo: formatDisplayDate(endDate),
        previousFrom: formatDisplayDate(prevStart),
        previousTo: formatDisplayDate(prevEnd),
      };
    })();
    return t("shopifyGeo.dateRange.showing", {
      currentFrom,
      currentTo,
      previousFrom,
      previousTo,
    });
  }, [data, startDate, endDate, t, formatDisplayDate]);

  return (
    <div
      ref={pdfContainerRef}
      className={[
        "shopify-geo",
        showFilterBar ? "shopify-geo--filtered" : "",
        `shopify-geo--layout-${layoutWidth}`,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showHeader ? (
        <DashboardSectionHeader
          className="shopify-geo__header"
          title={`${t("shopifyGeo.header.brand")} | ${t("shopifyGeo.header.title")}`}
          subtitle={dateRangeLabel}
          actionsClassName="dashboard-section-header__actions--filters"
          actions={
            <div className="dashboard-section-header__control-row">
              <div className="dashboard-section-header__control dashboard-section-header__control--wide">
                <OrganicDateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={todayISO}
                  onRangeChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
                    setStartDate(nextStartDate);
                    setEndDate(nextEndDate);
                  }}
                />
              </div>
              <DashboardExportButton
                dashboardName="Geo-Spatial"
                dateRange={{ startDate, endDate }}
                containerRef={pdfContainerRef}
                disabled={loading || Boolean(error)}
              />
            </div>
          }
        />
      ) : null}

      <div className="shopify-geo__toolbar">
        {showFilterBar ? (
          <section
            className="shopify-geo__active-filters"
            aria-label={t("shopifyGeo.crossFilter.sectionAria")}
          >
            <div className="shopify-geo__active-filters-head">
              <span className="shopify-geo__active-filters-title">
                {t("shopifyGeo.crossFilter.sectionTitle")}
              </span>
            </div>
            <div className="shopify-geo__active-filters-row">
              <div
                className="shopify-geo__active-filters-pills"
                title={filterChipText || undefined}
              >
                {filterRegionLabel ? (
                  <span className="shopify-geo__filter-pill shopify-geo__filter-pill--region">
                    {filterRegionLabel}
                    <button
                      type="button"
                      className="shopify-geo__filter-pill-remove"
                      onClick={removeRegionFilter}
                      aria-label={`Remove ${filterRegionLabel} filter`}
                    >
                      <X size={10} aria-hidden />
                    </button>
                  </span>
                ) : null}
                {resolvedCityDisplayName ? (
                  <span className="shopify-geo__filter-pill shopify-geo__filter-pill--city">
                    <MapPin size={12} aria-hidden />
                    {resolvedCityDisplayName}
                    <button
                      type="button"
                      className="shopify-geo__filter-pill-remove"
                      onClick={removeCityFilter}
                      aria-label={`Remove ${resolvedCityDisplayName} filter`}
                    >
                      <X size={10} aria-hidden />
                    </button>
                  </span>
                ) : null}
                {heatmapFilterLabel ? (
                  <span className="shopify-geo__filter-pill shopify-geo__filter-pill--time">
                    <Clock size={12} aria-hidden />
                    {heatmapFilterLabel}
                    <button
                      type="button"
                      className="shopify-geo__filter-pill-remove"
                      onClick={removeTimeFilter}
                      aria-label={`Remove ${heatmapFilterLabel} filter`}
                    >
                      <X size={10} aria-hidden />
                    </button>
                  </span>
                ) : null}
                {!filterChipText ? (
                  <span className="shopify-geo__filter-pill shopify-geo__filter-pill--loading">
                    {t("shopifyGeo.crossFilter.applying")}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="shopify-geo__active-filters-reset"
                onClick={() => setCrossFilter(null)}
              >
                {t("shopifyGeo.crossFilter.reset")}
              </button>
            </div>
          </section>
        ) : null}
      </div>

      <div ref={mainRef} className={`shopify-geo__main shopify-geo__main--${layoutWidth}`}>
        {showHeader ? (
          <div className="shopify-geo__narrative" data-pdf-block="narrative">
            <DashboardNarrativePanel
              narrative={narrative}
              loading={narrativePanelLoading}
              aiSummary={aiSummary}
            />
          </div>
        ) : null}
        {loading ? (
          <LoadingSpinner compact />
        ) : error ? (
          <div className="shopify-geo__error" role="status" aria-live="polite">
            {error}
          </div>
        ) : visibleRegions.length > 0 ? (
          <div
            className={[
              "shopify-geo__regions-scroll",
              visibleRegions.length === 1 ? "shopify-geo__regions-scroll--single" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div
              className={`shopify-geo__regions-grid ${
                visibleRegions.length === 1 ? "shopify-geo__regions-grid--single" : ""
              }`.trim()}
              data-pdf-block="geo-regions"
              data-pdf-splittable
              data-pdf-continue
            >
              {visibleRegions.map(region => (
                <RegionCard
                  key={inferPakistanRegionSlug(region.id, region.title)}
                  region={region}
                  crossFilter={crossFilter}
                  onCrossFilter={setCrossFilter}
                  weekDays={weekDays}
                  hourLabels={hourLabels}
                  mapSelectedCityNormalized={mapSelectedCityNormalized}
                  mapSelectedRegionId={mapSelectedRegionId}
                  isFocusedView={isFocusedFilterView}
                  heatmapFilterLabel={
                    activeRegionSlug &&
                    inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug
                      ? heatmapFilterLabel
                      : null
                  }
                  filteredCityDisplayName={
                    activeRegionSlug &&
                    inferPakistanRegionSlug(region.id, region.title) === activeRegionSlug
                      ? resolvedCityDisplayName
                      : null
                  }
                />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ShopifyGeoSpatialDashboard;
